import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCourses } from '../context/CourseContext';
import CourseProgress from '../components/courses/CourseProgress';
import Assessment from '../components/courses/Assessment';
import Certificate from '../components/courses/Certificate';
import { BookOpen, FileText, Download, Play, ArrowLeft } from 'lucide-react';
const CoursePage: React.FC = () => {
  const {
    courseId
  } = useParams<{
    courseId: string;
  }>();
  const navigate = useNavigate();
  const {
    courses,
    getCourseProgress,
    completeModule
  } = useCourses();
  const [activeTab, setActiveTab] = useState('content');
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null);
  const course = courses.find(c => c.id === courseId);
  const progress = courseId ? getCourseProgress(courseId) : undefined;
  useEffect(() => {
    if (course && course.modules.length > 0 && !activeModuleId) {
      setActiveModuleId(course.modules[0].id);
    }
  }, [course, activeModuleId]);
  if (!course || !courseId) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center" data-id="element-818">
        <h2 className="text-2xl font-bold text-gray-800 mb-4" data-id="element-819">
          Course Not Found
        </h2>
        <p className="text-gray-600 mb-8" data-id="element-820">
          The course you're looking for doesn't exist or has been removed.
        </p>
        <button onClick={() => navigate('/courses')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-821">
          <ArrowLeft className="mr-2 h-4 w-4" data-id="element-822" />
          Back to Courses
        </button>
      </div>;
  }
  if (!progress) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center" data-id="element-823">
        <h2 className="text-2xl font-bold text-gray-800 mb-4" data-id="element-824">Not Enrolled</h2>
        <p className="text-gray-600 mb-8" data-id="element-825">
          You're not enrolled in this course yet.
        </p>
        <button onClick={() => navigate('/courses')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-826">
          <ArrowLeft className="mr-2 h-4 w-4" data-id="element-827" />
          Back to Courses
        </button>
      </div>;
  }
  const activeModule = course.modules.find(m => m.id === activeModuleId);
  const handleCompleteModule = () => {
    if (activeModuleId) {
      completeModule(courseId, activeModuleId);
    }
  };
  const handlePreAssessmentComplete = () => {
    setActiveTab('content');
  };
  const handlePostAssessmentComplete = () => {
    setActiveTab('certificate');
  };
  const renderContent = () => {
    if (activeTab === 'pre-assessment') {
      return <Assessment courseId={courseId} type="pre" onComplete={handlePreAssessmentComplete} data-id="element-828" />;
    }
    if (activeTab === 'post-assessment') {
      return <Assessment courseId={courseId} type="post" onComplete={handlePostAssessmentComplete} data-id="element-829" />;
    }
    if (activeTab === 'certificate') {
      return <Certificate courseId={courseId} data-id="element-830" />;
    }
    // Course content tab
    if (!activeModule) {
      return <div className="text-gray-600" data-id="element-831">Select a module to begin learning.</div>;
    }
    return <div data-id="element-832">
        <h2 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-833">
          {activeModule.title}
        </h2>
        {activeModule.content.video && <div className="mb-8" data-id="element-834">
            <h3 className="text-lg font-medium text-gray-700 mb-3 flex items-center" data-id="element-835">
              <Play size={18} className="mr-2 text-blue-600" data-id="element-836" />
              Video Lecture
            </h3>
            <div className="aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-837">
              <div className="text-center p-8" data-id="element-838">
                <Play size={48} className="mx-auto mb-4 text-blue-600" data-id="element-839" />
                <p className="text-gray-500" data-id="element-840">
                  Video player would be embedded here
                </p>
              </div>
            </div>
          </div>}
        {activeModule.content.text && <div className="mb-8" data-id="element-841">
            <h3 className="text-lg font-medium text-gray-700 mb-3 flex items-center" data-id="element-842">
              <FileText size={18} className="mr-2 text-blue-600" data-id="element-843" />
              Lesson Content
            </h3>
            <div className="prose max-w-none bg-white p-6 rounded-lg shadow-sm" data-id="element-844">
              <p data-id="element-845">{activeModule.content.text}</p>
            </div>
          </div>}
        {activeModule.content.resources && activeModule.content.resources.length > 0 && <div className="mb-8" data-id="element-846">
              <h3 className="text-lg font-medium text-gray-700 mb-3 flex items-center" data-id="element-847">
                <Download size={18} className="mr-2 text-blue-600" data-id="element-848" />
                Resources
              </h3>
              <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-849">
                <ul className="divide-y divide-gray-200" data-id="element-850">
                  {activeModule.content.resources.map((resource, index) => <li key={index} className="py-3 flex justify-between items-center" data-id="element-851">
                      <span className="text-gray-700" data-id="element-852">{resource.name}</span>
                      <a href={resource.url} className="text-blue-600 hover:text-blue-800 flex items-center" target="_blank" rel="noopener noreferrer" data-id="element-853">
                        Download
                        <Download size={16} className="ml-1" data-id="element-854" />
                      </a>
                    </li>)}
                </ul>
              </div>
            </div>}
        <div className="mt-8 flex justify-between" data-id="element-855">
          <button onClick={() => navigate('/courses')} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-856">
            <ArrowLeft className="mr-2 h-4 w-4" data-id="element-857" />
            Back to Courses
          </button>
          <button onClick={handleCompleteModule} disabled={progress.completedModules.includes(activeModuleId || '')} className={`inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${progress.completedModules.includes(activeModuleId || '') ? 'bg-green-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`} data-id="element-858">
            {progress.completedModules.includes(activeModuleId || '') ? 'Completed' : 'Mark as Complete'}
          </button>
        </div>
      </div>;
  };
  const allModulesCompleted = course.modules.every(module => progress.completedModules.includes(module.id));
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-859">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-860">
        <div className="mb-8" data-id="element-861">
          <button onClick={() => navigate('/courses')} className="inline-flex items-center text-sm text-gray-600 hover:text-blue-600 mb-4" data-id="element-862">
            <ArrowLeft className="mr-1 h-4 w-4" data-id="element-863" />
            Back to All Courses
          </button>
          <h1 className="text-3xl font-bold text-gray-800 mb-2" data-id="element-864">
            {course.title}
          </h1>
          <p className="text-lg text-gray-600" data-id="element-865">{course.description}</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-866">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6" data-id="element-867">
            <CourseProgress courseId={courseId} data-id="element-868" />
            <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-869">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-870">
                Course Modules
              </h3>
              <nav className="space-y-1" data-id="element-871">
                {!progress.preAssessmentCompleted && <button onClick={() => setActiveTab('pre-assessment')} className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'pre-assessment' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:text-blue-700 hover:bg-blue-50'}`} data-id="element-872">
                    Pre-Assessment Quiz
                  </button>}
                {progress.preAssessmentCompleted && <>
                    <button onClick={() => {
                  setActiveTab('content');
                }} className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'content' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:text-blue-700 hover:bg-blue-50'}`} data-id="element-873">
                      <BookOpen className="mr-2 h-4 w-4" data-id="element-874" />
                      Course Content
                    </button>
                    <div className="pl-6 mt-2 space-y-1" data-id="element-875">
                      {course.modules.map(module => <button key={module.id} onClick={() => {
                    setActiveModuleId(module.id);
                    setActiveTab('content');
                  }} className={`w-full flex items-center px-3 py-2 text-sm rounded-md ${activeTab === 'content' && activeModuleId === module.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'} ${progress.completedModules.includes(module.id) ? 'text-green-600' : ''}`} data-id="element-876">
                          {progress.completedModules.includes(module.id) ? <div className="w-2 h-2 bg-green-500 rounded-full mr-2" data-id="element-877"></div> : <div className="w-2 h-2 bg-gray-300 rounded-full mr-2" data-id="element-878"></div>}
                          {module.title}
                        </button>)}
                    </div>
                    {allModulesCompleted && !progress.postAssessmentCompleted && <button onClick={() => setActiveTab('post-assessment')} className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'post-assessment' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:text-blue-700 hover:bg-blue-50'}`} data-id="element-879">
                          Post-Assessment Quiz
                        </button>}
                    {progress.postAssessmentCompleted && <button onClick={() => setActiveTab('certificate')} className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'certificate' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:text-blue-700 hover:bg-blue-50'}`} data-id="element-880">
                        Your Certificate
                      </button>}
                  </>}
              </nav>
            </div>
          </div>
          {/* Main Content */}
          <div className="lg:col-span-2" data-id="element-881">
            <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-882">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default CoursePage;